import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Users, AlertTriangle } from "lucide-react";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setPreviewError("This invite link is missing its token.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/team/invites/preview", {
          params: { token },
        });
        if (!cancelled) {
          setPreview(data);
          setName(data.name_hint || "");
        }
      } catch (err) {
        if (!cancelled) {
          setPreviewError(
            err?.response?.data?.detail || "This invite link is invalid."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { token, name };
      if (preview?.needs_password) {
        if (password.length < 6) {
          toast.error("Password must be at least 6 characters");
          setSubmitting(false);
          return;
        }
        payload.password = password;
      }
      const { data } = await api.post("/team/invites/accept", payload);
      if (data?.token) {
        localStorage.setItem("aip_token", data.token);
        await refresh();
      }
      toast.success("You're on the team");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="accept-invite-page"
      className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-6 py-16"
    >
      <div className="flex flex-col items-center text-center">
        <img
          src={H_LOGO_URL}
          alt="Hireloop PRO"
          className="h-16 w-16 object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
        <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          Team invitation
        </div>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Join the team
        </h1>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        </div>
      ) : previewError ? (
        <div
          data-testid="accept-invite-error"
          className="mt-10 flex flex-col items-center gap-3 border border-amber-200 bg-amber-50 px-6 py-8 text-center"
        >
          <AlertTriangle className="h-8 w-8 text-amber-700" />
          <p className="text-sm text-amber-900">{previewError}</p>
          <Link
            to="/login"
            className="mt-2 text-sm font-medium text-blue-700 hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <div
            data-testid="accept-invite-summary"
            className="mt-8 border border-slate-200 bg-slate-50 px-5 py-4"
          >
            <div className="flex items-start gap-3">
              <Users className="mt-0.5 h-5 w-5 text-blue-700" />
              <div className="text-sm text-black">
                <strong>{preview.owner_name}</strong>
                {preview.owner_company ? (
                  <>
                    {" "}from <strong>{preview.owner_company}</strong>
                  </>
                ) : null}{" "}
                invited <strong>{preview.email}</strong> to their Hireloop PRO team.
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label htmlFor="invite-name" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
                Your name
              </Label>
              <Input
                id="invite-name"
                data-testid="accept-invite-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 rounded-sm border-slate-300"
                placeholder="Alex Carter"
              />
            </div>
            {preview?.needs_password && (
              <div>
                <Label htmlFor="invite-password" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
                  Choose a password
                </Label>
                <Input
                  id="invite-password"
                  data-testid="accept-invite-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 rounded-sm border-slate-300"
                />
              </div>
            )}
            <Button
              type="submit"
              data-testid="accept-invite-submit-btn"
              disabled={submitting}
              className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Accept &amp; join team
            </Button>
          </form>
        </>
      )}

      <p className="mt-8 text-center text-sm text-slate-500">
        Wrong account?{" "}
        <Link
          to="/login"
          className="font-medium text-blue-700 hover:underline"
        >
          Sign in here
        </Link>
      </p>
    </main>
  );
}
